import { assert } from "@msys/common";
import { Modal } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { cloneDeep, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import {
  AllowedValuesFormValues,
  useAllowedValuesFieldValidationSchema,
} from "../form-fields/PropertyAllowedValuesField.js";
import { PropertyAllowedValuesFieldFromTemplateType } from "../form-fields/PropertyAllowedValuesFieldFromTemplateType.js";
import {
  useValuesMediaBoolFieldValidationSchema,
  ValuesMediaBoolField,
  ValuesMediaBoolFormValues,
} from "../form-fields/PropertyValuesMediaBoolField.js";
import {
  NumberRangeSection,
  NumberRangeSectionFormValues,
  useNumberRangeValidationSchemaWithTemplateType,
} from "../NumberRangeSection.js";
import {
  PropValues,
  TemplateTypePropValues,
  TypeFields,
} from "../properties.js";

type FormValues = TypeFields &
  PropValues &
  AllowedValuesFormValues &
  ValuesMediaBoolFormValues &
  NumberRangeSectionFormValues & {
    isComputed: boolean;
    templateTypePropValues?: TemplateTypePropValues;
  };

interface Props {
  values: FormValues;
  handleClose: () => void;
  handleComplete: (values: FormValues) => void | Promise<void>;
}

export const PropertyFromTemplateTypeAllowedValuesModal = ({
  values,
  handleComplete,
  handleClose,
}: Props) => {
  assert(values.templateTypePropValues, "No template type PropValues");

  const { t } = useTranslate(["Global", "QuoteItem"]);

  const initialValues = React.useMemo(() => cloneDeep(values), [values]);

  const allowedValuesSchema = useAllowedValuesFieldValidationSchema(values);
  const valuesMediaBoolSchema = useValuesMediaBoolFieldValidationSchema();
  const numberRangeSchema = useNumberRangeValidationSchemaWithTemplateType(
    values.templateTypePropValues
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        ...allowedValuesSchema,
        ...valuesMediaBoolSchema,
        ...numberRangeSchema,
      }),
    [allowedValuesSchema, numberRangeSchema, valuesMediaBoolSchema]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  const handleSubmit = async (values: FormValues) => {
    await handleComplete(values);
    handleClose();
  };

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {formikProps => (
        <Modal
          title={t("Allowed values", { ns: "QuoteItem" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Save", { ns: "Global" }),
              buttonProps: {
                form: formId,
                type: "submit",
                loading: formikProps.isSubmitting,
                disabled: !formikProps.isValid || !formikProps.dirty,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack direction="column" spacing={1}>
              {formikProps.values.type !== "BOOLEAN" && !values.isComputed && (
                <PropertyAllowedValuesFieldFromTemplateType
                  type={formikProps.values.type}
                  disabled={
                    (formikProps.values.type === "NUMBER" &&
                      (formikProps.values.setRange ||
                        formikProps.values.setStep)) ||
                    formikProps.isSubmitting
                  }
                  templateTypeAllowedValues={
                    values.templateTypePropValues!.templateTypeAllowedValues
                  }
                  excludedType={values.templateTypePropValues!.propertyType}
                  excludedAllowedValues={
                    values.templateTypePropValues!.propertyExcludedAllowedValues
                  }
                />
              )}

              {formikProps.values.type === "BOOLEAN" && !values.isComputed && (
                <ValuesMediaBoolField disabled={formikProps.isSubmitting} />
              )}
              {formikProps.values.type === "NUMBER" && !values.isComputed && (
                <NumberRangeSection
                  disabledSetRange={
                    values.templateTypePropValues!.hasDefinedRange
                  }
                  disabledSetStep={
                    values.templateTypePropValues!.hasDefinedStep
                  }
                  disabled={
                    formikProps.values.defineAllowedValues ||
                    formikProps.isSubmitting
                  }
                />
              )}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
